import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { clearCrmSessionToken, crmApiBase, getCrmSessionToken, isCrmPortalMode } from './crmEnv';
import { API_BASE_URL } from '../../../lib/apiBaseUrl';

// ─────────────────────────────────────────────────────────────────────────────
// API Base URL
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE = API_BASE_URL;

// Global CRM request activity tracker (used for UI loading indicators)
let crmActiveRequestCount = 0;
let crmLastRequestMeta = null;
const crmNetworkSubscribers = new Set();

function getCrmNetworkSnapshot() {
    return {
        activeCount: crmActiveRequestCount,
        busy: crmActiveRequestCount > 0,
        lastRequest: crmLastRequestMeta,
    };
}

function notifyCrmNetworkSubscribers() {
    const snapshot = getCrmNetworkSnapshot();
    crmNetworkSubscribers.forEach((listener) => {
        try {
            listener(snapshot);
        } catch {
            // ignore subscriber errors
        }
    });
    if (typeof window !== 'undefined') {
        try {
            window.dispatchEvent(new CustomEvent('crm-network-activity', { detail: snapshot }));
        } catch {
            // ignore custom event failures
        }
    }
}

function startCrmNetworkRequest(meta) {
    crmActiveRequestCount += 1;
    crmLastRequestMeta = {
        method: String(meta?.method || 'GET').toUpperCase(),
        endpoint: String(meta?.endpoint || ''),
        label: typeof meta?.label === 'string' && meta.label.trim() ? meta.label.trim() : null,
        activity: typeof meta?.activity === 'string' && meta.activity.trim() ? meta.activity.trim() : null,
        startedAt: Date.now(),
    };
    notifyCrmNetworkSubscribers();

    let closed = false;
    return () => {
        if (closed) return;
        closed = true;
        crmActiveRequestCount = Math.max(0, crmActiveRequestCount - 1);
        notifyCrmNetworkSubscribers();
    };
}

export function subscribeCrmNetworkActivity(listener) {
    if (typeof listener !== 'function') return () => {};
    crmNetworkSubscribers.add(listener);
    listener(getCrmNetworkSnapshot());
    return () => {
        crmNetworkSubscribers.delete(listener);
    };
}

export function getCrmNetworkActivitySnapshot() {
    return getCrmNetworkSnapshot();
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get Firebase auth token for the current user
 * Waits for auth state if user is not immediately available
 */
async function getAuthToken() {
    const auth = firebase.auth();

    // If user is already available, get token immediately
    if (auth.currentUser) {
        return auth.currentUser.getIdToken();
    }

    // Wait for auth state to be resolved
    return new Promise((resolve, reject) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            if (user) {
                user.getIdToken()
                    .then(resolve)
                    .catch(reject);
            } else {
                reject(new Error('User not authenticated'));
            }
        });

        // Timeout after 10 seconds
        setTimeout(() => {
            unsubscribe();
            reject(new Error('Authentication timeout'));
        }, 10000);
    });
}

function shouldHandlePortalSessionInvalidation(errorData) {
    const nestedError = errorData?.error;
    const rawCode = String(
        (typeof nestedError === 'string' ? nestedError : nestedError?.code || nestedError?.message)
        || errorData?.message
        || '',
    ).trim();
    if (!rawCode) return false;
    const normalized = rawCode.toUpperCase().replace(/\s+/g, '_');
    return normalized === 'SESSION_REPLACED'
        || normalized === 'SEAT_DISABLED'
        || normalized === 'MISSING_CRM_SESSION'
        || normalized === 'INVALID_OR_EXPIRED_SESSION';
}

function handlePortalUnauthorized(errorData) {
    if (!shouldHandlePortalSessionInvalidation(errorData)) {
        return false;
    }
    clearCrmSessionToken();
    if (typeof window !== 'undefined') {
        window.localStorage.setItem('crmSessionReplaced', new Date().toISOString());
        if (!window.location.pathname.startsWith('/crm-portal/login')) {
            window.location.href = '/crm-portal/login';
        }
    }
    return true;
}

function buildApiError(errorData, fallbackMessage = 'Request failed') {
    let message = fallbackMessage;
    let code = null;
    let requestId = null;

    if (errorData && typeof errorData === 'object') {
        const nestedError = errorData.error;
        if (typeof nestedError === 'string' && nestedError.trim()) {
            message = nestedError.trim();
        } else if (nestedError && typeof nestedError === 'object') {
            if (typeof nestedError.message === 'string' && nestedError.message.trim()) {
                message = nestedError.message.trim();
            }
            if (typeof nestedError.code === 'string' && nestedError.code.trim()) {
                code = nestedError.code.trim();
            }
            if (typeof nestedError.requestId === 'string' && nestedError.requestId.trim()) {
                requestId = nestedError.requestId.trim();
            }
        }

        if (typeof errorData.message === 'string' && errorData.message.trim()) {
            message = errorData.message.trim();
        }
        if (typeof errorData.code === 'string' && errorData.code.trim()) {
            code = errorData.code.trim();
        }
    }

    const finalMessage = requestId
        ? `${message} (RequestId: ${requestId})`
        : message;
    const err = new Error(finalMessage);
    err.code = code || undefined;
    err.requestId = requestId || undefined;
    return err;
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
    const {
        crmNetworkLabel,
        crmNetworkActivity,
        crmNetworkSilent,
        ...requestOptions
    } = options || {};
    const stopTracking = crmNetworkSilent
        ? () => {}
        : startCrmNetworkRequest({
            endpoint,
            method: requestOptions?.method || 'GET',
            label: crmNetworkLabel,
            activity: crmNetworkActivity,
        });
    try {
    const isPortal = isCrmPortalMode();
    const headers = {
        ...(requestOptions.headers || {}),
    };

    if (isPortal) {
        const sessionToken = getCrmSessionToken();
        if (!sessionToken) {
            throw new Error('Missing CRM session');
        }
        headers['X-CRM-SESSION'] = sessionToken;
        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }
    } else {
        const token = await getAuthToken();
        headers['Content-Type'] = 'application/json';
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...requestOptions,
        headers,
    };

    const response = await fetch(`${API_BASE}${crmApiBase()}${endpoint}`, config);

    if (!response.ok) {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch {
            // Ignore JSON parse errors
        }
        if (isPortal && response.status === 401 && handlePortalUnauthorized(errorData)) {
            throw new Error(errorData?.error || 'SESSION_REPLACED');
        }
        throw buildApiError(errorData);
    }

    return response.json();
    } finally {
        stopTracking();
    }
}

async function apiRequestRaw(endpoint, options = {}) {
    const {
        crmNetworkLabel,
        crmNetworkActivity,
        crmNetworkSilent,
        ...requestOptions
    } = options || {};
    const stopTracking = crmNetworkSilent
        ? () => {}
        : startCrmNetworkRequest({
            endpoint,
            method: requestOptions?.method || 'GET',
            label: crmNetworkLabel,
            activity: crmNetworkActivity,
        });
    try {
    const isPortal = isCrmPortalMode();
    const headers = {
        ...(requestOptions.headers || {}),
    };

    if (isPortal) {
        const sessionToken = getCrmSessionToken();
        if (!sessionToken) {
            throw new Error('Missing CRM session');
        }
        headers['X-CRM-SESSION'] = sessionToken;
    } else {
        const token = await getAuthToken();
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...requestOptions,
        headers,
    };

    const response = await fetch(`${API_BASE}${crmApiBase()}${endpoint}`, config);

    if (!response.ok) {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch {
            // Ignore JSON parse errors
        }
        if (isPortal && response.status === 401 && handlePortalUnauthorized(errorData)) {
            throw new Error(errorData?.error || 'SESSION_REPLACED');
        }
        throw buildApiError(errorData);
    }

    return response;
    } finally {
        stopTracking();
    }
}

async function apiRequestAbsolute(path, options = {}) {
    const {
        crmNetworkLabel,
        crmNetworkActivity,
        crmNetworkSilent,
        ...requestOptions
    } = options || {};
    const stopTracking = crmNetworkSilent
        ? () => {}
        : startCrmNetworkRequest({
            endpoint: path,
            method: requestOptions?.method || 'GET',
            label: crmNetworkLabel,
            activity: crmNetworkActivity,
        });
    try {
    const isPortal = isCrmPortalMode();
    const headers = {
        ...(requestOptions.headers || {}),
    };

    if (isPortal) {
        const sessionToken = getCrmSessionToken();
        if (!sessionToken) {
            throw new Error('Missing CRM session');
        }
        headers['X-CRM-SESSION'] = sessionToken;
        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }
    } else {
        const token = await getAuthToken();
        headers['Content-Type'] = 'application/json';
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...requestOptions,
        headers,
    };

    const response = await fetch(`${API_BASE}${path}`, config);
    if (!response.ok) {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch {
            // Ignore JSON parse errors
        }
        if (isPortal && response.status === 401 && handlePortalUnauthorized(errorData)) {
            throw new Error(errorData?.error || 'SESSION_REPLACED');
        }
        throw buildApiError(errorData);
    }

    return response.json();
    } finally {
        stopTracking();
    }
}

/**
 * Build query string from params object
 */
function buildQueryString(params) {
    const filtered = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);

    return filtered.length > 0 ? `?${filtered.join('&')}` : '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard API
// ─────────────────────────────────────────────────────────────────────────────

export async function getDashboardStats(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/dashboard${queryString}`, options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Leads API
// ─────────────────────────────────────────────────────────────────────────────

export async function getLeads(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/leads${queryString}`, options);
}

export async function getLeadViews() {
    return apiRequest('/leads/views');
}

export async function saveLeadView(view) {
    return apiRequest('/leads/views', {
        method: 'POST',
        body: JSON.stringify(view),
    });
}

export async function deleteLeadView(viewId) {
    return apiRequest(`/leads/views/${viewId}`, {
        method: 'DELETE',
    });
}

export async function getLead(id, options = {}) {
    return apiRequest(`/leads/${id}`, options);
}

export async function getLeadDetail(id, options = {}) {
    return apiRequest(`/leads/${id}/detail`, options);
}

export async function getLeadMatches(id, params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/leads/${id}/matches${queryString}`);
}

export async function queryLeadMatches(id, payload = {}, params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/leads/${id}/matches/query${queryString}`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function recomputeLeadMatches(id) {
    return apiRequest(`/leads/${id}/matches/refresh`, {
        method: 'POST',
    });
}

export async function createLead(data) {
    return apiRequest('/leads', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateLead(id, data) {
    return apiRequest(`/leads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteLead(id) {
    return apiRequest(`/leads/${id}`, {
        method: 'DELETE',
    });
}

export async function convertLead(id, options = {}) {
    return apiRequest(`/leads/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify(options),
    });
}

export async function createSiteVisit(leadId, payload) {
    return apiRequest(`/leads/${leadId}/site-visits`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function confirmSiteVisit(leadId, activityId) {
    return apiRequest(`/leads/${leadId}/site-visits/${activityId}/confirm`, {
        method: 'POST',
    });
}

export async function completeSiteVisit(leadId, activityId, payload) {
    return apiRequest(`/leads/${leadId}/site-visits/${activityId}/complete`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function getLeadFollowups(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/leads/followups${queryString}`);
}

export async function logLeadCommunication(leadId, payload) {
    return apiRequest(`/leads/${leadId}/log`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function createDealFromMatch(leadId, payload) {
    return apiRequest(`/leads/${leadId}/create-deal-from-match`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function executeLeadAction(leadId, payload, idempotencyKey) {
    const actionType = String(payload?.type || '').trim().toLowerCase();
    const isLoggingAction = actionType === 'log_call'
        || actionType === 'send_email'
        || actionType === 'send_whatsapp';
    return apiRequest(`/leads/${leadId}/actions`, {
        method: 'POST',
        headers: idempotencyKey ? { 'X-IDEMPOTENCY-KEY': idempotencyKey } : undefined,
        body: JSON.stringify(payload),
        crmNetworkActivity: isLoggingAction ? 'log' : undefined,
        crmNetworkLabel: isLoggingAction ? 'Logging...' : undefined,
        crmNetworkSilent: isLoggingAction,
    });
}

// Lead Sources / Integrations API
export async function createLeadSourceIntegration(payload) {
    return apiRequestAbsolute('/api/integrations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function getLeadSourceIntegrations(franchiseId) {
    const queryString = buildQueryString({ franchiseId });
    return apiRequestAbsolute(`/api/integrations${queryString}`);
}

export async function deleteLeadSourceIntegration(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}`, {
        method: 'DELETE',
    });
}

export async function pauseLeadSourceIntegration(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/pause`, {
        method: 'POST',
    });
}

export async function resumeLeadSourceIntegration(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/resume`, {
        method: 'POST',
    });
}

export async function testLeadSourceIntegration(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/test`, {
        method: 'POST',
    });
}

export async function startGoogleSheetsLeadSource(payload) {
    return apiRequestAbsolute('/api/integrations/google-sheets/start', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function startMetaLeadSource(payload = {}) {
    return apiRequestAbsolute('/api/integrations/meta/start', {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function listMetaLeadSourcePages() {
    return apiRequestAbsolute('/api/integrations/meta/pages', {
        method: 'GET',
    });
}

export async function listMetaLeadSourceForms(pageId) {
    return apiRequestAbsolute(`/api/integrations/meta/pages/${encodeURIComponent(pageId)}/forms`, {
        method: 'GET',
    });
}

export async function getMetaLeadSourceFormSample(formId, params = {}) {
    const queryString = buildQueryString({
        pageId: params.pageId,
    });
    return apiRequestAbsolute(`/api/integrations/meta/forms/${encodeURIComponent(formId)}/sample${queryString}`, {
        method: 'GET',
    });
}

export async function createMetaLeadSource(payload) {
    return apiRequestAbsolute('/api/integrations/meta/create', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function configureMetaLeadSource(integrationId, payload) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/meta/configure`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function backfillMetaLeadSource(integrationId, payload = {}) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/meta/backfill`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function testMetaLeadSource(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/meta/test`, {
        method: 'POST',
    });
}

export async function listMetaLeadSourceFailedEvents(integrationId, params = {}) {
    const queryString = buildQueryString({ limit: params.limit });
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/meta/failed-events${queryString}`, {
        method: 'GET',
    });
}

export async function replayMetaLeadSourceFailedEvent(integrationId, eventId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/meta/events/${encodeURIComponent(eventId)}/replay`, {
        method: 'POST',
    });
}

export async function configureGoogleSheetsLeadSource(integrationId, payload) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/config`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function runGoogleSheetsLeadSourceSync(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/run-sync`, {
        method: 'POST',
    });
}

export async function setupGoogleSheetsLeadSourcePush(integrationId) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/push/setup`, {
        method: 'POST',
    });
}

export async function testGoogleSheetsLeadSourcePush(integrationId, payload = {}) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/push/test`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function listGoogleSheetsLeadSourceSpreadsheets(integrationId, params = {}) {
    const queryString = buildQueryString({
        q: params.q,
        spreadsheetId: params.spreadsheetId,
    });
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/spreadsheets${queryString}`, {
        method: 'GET',
    });
}

export async function createGoogleSheetsLeadSourceSpreadsheet(integrationId, payload = {}) {
    return apiRequestAbsolute(`/api/integrations/${encodeURIComponent(integrationId)}/google-sheets/spreadsheets/create`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Automations API
// ─────────────────────────────────────────────────────────────────────────────

export async function getAutomations() {
    return apiRequest('/automations');
}

export async function createAutomation(payload) {
    return apiRequest('/automations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function updateAutomation(id, payload) {
    return apiRequest(`/automations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

export async function toggleAutomation(id, enabled) {
    return apiRequest(`/automations/${id}/toggle`, {
        method: 'POST',
        body: enabled === undefined ? undefined : JSON.stringify({ enabled }),
    });
}

export async function getAutomationLogs(range = '7d') {
    const queryString = buildQueryString({ range });
    return apiRequest(`/automations/logs${queryString}`);
}

export async function testAutomation(payload) {
    return apiRequest('/automations/test', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function getAutomationTemplates(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/automation/templates${queryString}`);
}

export async function createAutomationTemplate(payload) {
    return apiRequest('/automation/templates', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function updateAutomationTemplate(id, payload) {
    return apiRequest(`/automation/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

export async function deleteAutomationTemplate(id) {
    return apiRequest(`/automation/templates/${id}`, {
        method: 'DELETE',
    });
}

export async function previewAutomationTemplate(id, payload) {
    return apiRequest(`/automation/templates/${id}/preview`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function getAutomationOutbox(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/automation/outbox${queryString}`);
}

export async function getAutomationOutboxMessage(messageId) {
    return apiRequest(`/automation/outbox/${messageId}`);
}

export async function approveOutboxMessage(messageId) {
    return apiRequest(`/automation/outbox/${messageId}/approve`, {
        method: 'POST',
    });
}

export async function cancelOutboxMessage(messageId) {
    return apiRequest(`/automation/outbox/${messageId}/cancel`, {
        method: 'POST',
    });
}

export async function retryOutboxMessage(messageId) {
    return apiRequest(`/automation/outbox/${messageId}/retry`, {
        method: 'POST',
    });
}

export async function markOutboxMessageSent(messageId) {
    return apiRequest(`/automation/outbox/${messageId}/mark-sent`, {
        method: 'POST',
    });
}

export async function getAutomationSettings() {
    return apiRequest('/automation/settings');
}

export async function saveAutomationSettings(payload) {
    return apiRequest('/automation/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

export async function getAutomationPlaybooks() {
    return apiRequest('/automation/playbooks');
}

export async function updateAutomationPlaybook(id, payload) {
    return apiRequest(`/automation/playbooks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Contacts API
// ─────────────────────────────────────────────────────────────────────────────

export async function getContacts(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/contacts${queryString}`, options);
}

export async function getContact(id, options = {}) {
    return apiRequest(`/contacts/${id}`, options);
}

export async function createContact(data) {
    return apiRequest('/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateContact(id, data) {
    return apiRequest(`/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteContact(id) {
    return apiRequest(`/contacts/${id}`, {
        method: 'DELETE',
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Deals API
// ─────────────────────────────────────────────────────────────────────────────

export async function getDeals(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/deals${queryString}`, options);
}

export async function getDealsPipeline(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/deals/pipeline${queryString}`, options);
}

export async function getDealsDueBuckets(params = {}, options = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/deals/due-buckets${queryString}`, options);
}

export async function getDeal(id, options = {}) {
    return apiRequest(`/deals/${id}`, options);
}

export async function createDeal(data) {
    return apiRequest('/deals', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateDeal(id, data) {
    return apiRequest(`/deals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteDeal(id) {
    return apiRequest(`/deals/${id}`, {
        method: 'DELETE',
    });
}

export async function getLeadDeals(leadId, params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/leads/${leadId}/deals${queryString}`);
}

export async function createLeadDeal(leadId, data) {
    return apiRequest(`/leads/${leadId}/deals`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function linkLeadDeal(leadId, dealId) {
    return apiRequest(`/leads/${leadId}/deals/${dealId}/link`, {
        method: 'POST',
    });
}

export async function unlinkLeadDeal(leadId, dealId) {
    return apiRequest(`/leads/${leadId}/deals/${dealId}/unlink`, {
        method: 'POST',
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────────
// Properties API
// ────────────────────────────────────────────────────────────────────────────────

export async function getProperties(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/properties${queryString}`);
}

export async function getProperty(id) {
    return apiRequest(`/properties/${id}`);
}

export async function getPropertyMatches(id, params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/properties/${id}/matches${queryString}`);
}

export async function refreshPropertyMatches(id) {
    return apiRequest(`/properties/${id}/matches/refresh`, {
        method: 'POST',
    });
}

export async function createProperty(data) {
    return apiRequest('/properties', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateProperty(id, data) {
    return apiRequest(`/properties/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteProperty(id) {
    return apiRequest(`/properties/${id}`, {
        method: 'DELETE',
    });
}

export async function createPropHuntAd(payload) {
    return apiRequestAbsolute('/api/prophunt/createAd', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function syncPropHuntStatus(payload) {
    return apiRequestAbsolute('/api/prophunt/syncStatus', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function getPropHuntPropertyLeads(propertyId, params = {}) {
    const queryString = buildQueryString(params);
    return apiRequestAbsolute(`/api/prophunt/propertyLeads/${encodeURIComponent(propertyId)}${queryString}`);
}

// Files API
export async function getFiles(entityType, entityId) {
    const queryString = buildQueryString({ entityType, entityId });
    return apiRequest(`/files${queryString}`);
}

export async function uploadFile(payload) {
    return apiRequest('/files/upload', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function downloadFile(fileId) {
    const response = await apiRequestRaw(`/files/download?fileId=${encodeURIComponent(fileId)}`);
    return response.blob();
}

export async function getAudit(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/audit${queryString}`);
}


// Duplicates + Merge
export async function getLeadDuplicates() {
    return apiRequest('/leads/duplicates');
}

export async function getContactDuplicates() {
    return apiRequest('/contacts/duplicates');
}

export async function mergeLeads(payload) {
    return apiRequest('/leads/merge', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function mergeContacts(payload) {
    return apiRequest('/contacts/merge', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// Import / Export
export async function exportLeads(params = {}) {
    const queryString = buildQueryString(params);
    const response = await apiRequestRaw(`/leads/export.csv${queryString}`);
    return response.blob();
}

export async function exportContacts(params = {}) {
    const queryString = buildQueryString(params);
    const response = await apiRequestRaw(`/contacts/export.csv${queryString}`);
    return response.blob();
}

export async function importLeads(payload) {
    return apiRequest('/leads/import', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function importContacts(payload) {
    return apiRequest('/contacts/import', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// Activities API
// ─────────────────────────────────────────────────────────────────────────────

export async function getActivities(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/activities${queryString}`);
}

export async function getActivitiesCalendar(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/activities/calendar-v2${queryString}`);
}

export async function getActivitiesWeek(params = {}) {
    const queryString = buildQueryString(params);
    return apiRequest(`/activities/week${queryString}`);
}

export async function getActivity(id) {
    return apiRequest(`/activities/${id}`);
}

export async function createActivity(data) {
    return apiRequest('/activities', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function createLogActivity(data) {
    return apiRequest('/activities/log', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function updateActivity(id, data) {
    return apiRequest(`/activities/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteActivity(id) {
    return apiRequest(`/activities/${id}`, {
        method: 'DELETE',
    });
}

export async function completeActivity(id) {
    return apiRequest(`/activities/${id}/complete`, {
        method: 'POST',
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Team API (Owner Dashboard Only)
// ─────────────────────────────────────────────────────────────────────────────

export async function getTeam() {
    return apiRequest('/portal/team');
}

export async function inviteTeammate(data) {
    return apiRequest('/portal/invite', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function resetSeatPin(seatId) {
    return apiRequest('/portal/reset-pin', {
        method: 'POST',
        body: JSON.stringify({ seatId }),
    });
}

export async function removeSeat(seatId) {
    return apiRequest('/portal/remove-seat', {
        method: 'POST',
        body: JSON.stringify({ seatId }),
    });
}

export async function revokeInvite(token) {
    return apiRequest('/portal/invite/revoke', {
        method: 'POST',
        body: JSON.stringify({ inviteId: token }),
    });
}

export async function regenerateInvite(inviteId) {
    return apiRequest('/portal/invite/regenerate', {
        method: 'POST',
        body: JSON.stringify({ inviteId }),
    });
}

export async function disableSeat(seatId) {
    return apiRequest('/portal/seat/disable', {
        method: 'POST',
        body: JSON.stringify({ seatId }),
    });
}
